import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import CompanyEditor from "@/components/CompanyEditor";
import { Plus, Building2, Pause, Play, Archive as ArchiveIcon, Trash2, MoreHorizontal, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Companies() {
  const { user, setActiveCompanyId } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ legal_name: "", trading_name: "", industry: "", website: "", country: "", timezone: "UTC", primary_email: "" });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingHardDelete, setPendingHardDelete] = useState(null);
  const [hardConfirmText, setHardConfirmText] = useState("");

  const canManage = user?.role === "super_admin";

  const setStatus = async (c, status) => {
    try {
      await api.patch(`/companies/${c.id}`, { status });
      toast.success(`${c.trading_name || c.legal_name} ${status === "active" ? "reactivated" : status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/companies/${pendingDelete.id}`);
      toast.success(`Archived ${pendingDelete.trading_name || pendingDelete.legal_name}`);
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const doHardDelete = async () => {
    if (!pendingHardDelete) return;
    if (hardConfirmText.trim() !== (pendingHardDelete.legal_name || "").trim()) {
      toast.error("Confirmation text does not match the legal name.");
      return;
    }
    try {
      const { data } = await api.delete(`/companies/${pendingHardDelete.id}`, {
        params: { hard: true, confirm: pendingHardDelete.legal_name },
      });
      const wiped = Object.values(data?.cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(`Permanently deleted — ${wiped} related records removed`);
      setPendingHardDelete(null); setHardConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    const { data } = await api.get("/companies");
    setCompanies(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/companies", form);
      toast.success(`Company "${data.legal_name}" created`);
      setForm({ legal_name: "", trading_name: "", industry: "", website: "", country: "", timezone: "UTC", primary_email: "" });
      setOpen(false);
      await load();
      setActiveCompanyId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create company");
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="companies-page">
      <PageHeader
        title="Companies"
        subtitle="Every mailbox, agent, and record lives inside a company. Manage tenants here."
        actions={
          user?.role === "super_admin" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="new-company-button">
                  <Plus className="h-4 w-4 mr-1.5" /> New company
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl tracking-tight">Create company</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <FormRow label="Legal name" required>
                    <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} required data-testid="company-legal-name-input" />
                  </FormRow>
                  <FormRow label="Trading name">
                    <Input value={form.trading_name} onChange={(e) => setForm({ ...form, trading_name: e.target.value })} data-testid="company-trading-name-input" />
                  </FormRow>
                  <FormRow label="Primary email">
                    <Input value={form.primary_email} onChange={(e) => setForm({ ...form, primary_email: e.target.value })} data-testid="company-primary-email-input" />
                  </FormRow>
                  <div className="grid grid-cols-2 gap-3">
                    <FormRow label="Industry">
                      <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="company-industry-input" />
                    </FormRow>
                    <FormRow label="Country">
                      <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="company-country-input" />
                    </FormRow>
                  </div>
                  <FormRow label="Website">
                    <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} data-testid="company-website-input" />
                  </FormRow>
                  <DialogFooter>
                    <Button type="submit" disabled={loading} className="rounded-sm" data-testid="company-create-submit">Create</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />
      <div className="p-6 lg:p-8">
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="tbl w-full" data-testid="companies-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Industry</th>
                <th>Country</th>
                <th>Status</th>
                <th>Timezone</th>
                <th>Users</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="h-7 w-7 rounded-sm bg-terracotta/15 text-terracotta grid place-items-center">
                        <Building2 className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{c.trading_name || c.legal_name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">{c.legal_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-muted-foreground">{c.industry || "—"}</td>
                  <td className="text-muted-foreground">{c.country || "—"}</td>
                  <td>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${c.status === "active" ? "bg-emerald-deep/15 text-emerald-deep" : "bg-muted text-muted-foreground"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-muted-foreground">{c.timezone}</td>
                  <td className="font-mono text-xs">{c.user_limit}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/companies/${c.id}`}>
                        <Button variant="ghost" size="sm" className="text-xs" data-testid={`view-${c.id}`}>
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                        </Button>
                      </Link>
                      {canManage && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setEditing(c)} className="text-xs" data-testid={`edit-${c.id}`}>Edit</Button>
                          <Button variant="ghost" size="sm" onClick={() => setActiveCompanyId(c.id)} className="text-xs" data-testid={`switch-to-${c.id}`}>Switch</Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`more-${c.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rounded-xl w-44">
                              {c.status !== "active" && (
                                <DropdownMenuItem onClick={() => setStatus(c, "active")} data-testid={`reactivate-${c.id}`}>
                                  <Play className="h-3.5 w-3.5 mr-2 text-emerald-brand" /> Reactivate
                                </DropdownMenuItem>
                              )}
                              {c.status !== "suspended" && (
                                <DropdownMenuItem onClick={() => setStatus(c, "suspended")} data-testid={`suspend-${c.id}`}>
                                  <Pause className="h-3.5 w-3.5 mr-2 text-yellow-600" /> Suspend
                                </DropdownMenuItem>
                              )}
                              {c.status !== "archived" && (
                                <DropdownMenuItem onClick={() => setStatus(c, "archived")} data-testid={`archive-${c.id}`}>
                                  <ArchiveIcon className="h-3.5 w-3.5 mr-2 text-muted-foreground" /> Archive
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setPendingDelete(c)} className="text-crimson focus:text-crimson" data-testid={`delete-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Archive (soft)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setPendingHardDelete(c); setHardConfirmText(""); }} className="text-crimson focus:text-crimson" data-testid={`hard-delete-${c.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete permanently
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {companies.length === 0 && (
                <tr><td colSpan="7" className="text-center text-muted-foreground py-8">No companies yet. Create your first tenant.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <CompanyEditor company={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={load} />
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-company-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Delete this company?</AlertDialogTitle>
            <AlertDialogDescription>
              This archives <span className="font-mono">{pendingDelete?.trading_name || pendingDelete?.legal_name}</span> and hides
              it from active workspaces. Existing emails, tickets and contacts stay in the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-crimson hover:bg-crimson/90" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard delete confirmation */}
      <AlertDialog open={!!pendingHardDelete} onOpenChange={(o) => { if (!o) { setPendingHardDelete(null); setHardConfirmText(""); } }}>
        <AlertDialogContent className="rounded-2xl" data-testid="hard-delete-company-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight text-crimson">
              Permanently delete this company?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div>
                  This is <span className="font-semibold">irreversible</span>. It will wipe the company row, all its
                  mailboxes, emails, contacts, tickets, campaigns, signatures, and audit logs.
                </div>
                <div>
                  To confirm, type the legal name exactly:{" "}
                  <span className="font-mono text-foreground">{pendingHardDelete?.legal_name}</span>
                </div>
                <Input
                  value={hardConfirmText}
                  onChange={(e) => setHardConfirmText(e.target.value)}
                  placeholder={pendingHardDelete?.legal_name}
                  className="rounded-sm"
                  data-testid="hard-delete-confirm-input"
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" onClick={() => setHardConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-crimson hover:bg-crimson/90 disabled:opacity-50"
              onClick={doHardDelete}
              disabled={hardConfirmText.trim() !== (pendingHardDelete?.legal_name || "").trim()}
              data-testid="hard-delete-confirm-button"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FormRow({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
